/**
 * useKartotekaRenderField — silnik formatek: generyczny hook renderowania pól
 * core+ext dowolnej kartoteki (keystone unifikacja-formatek, Krok 1).
 *
 * Uogólnienie `useDocumentRenderField` pilota (Z2, ADDYTYWNIE): cała logika
 * renderowania (query metadanych pól, fallback 403, indeks slug+id, routing
 * core przez config, delegacja ext do ExtFieldValueInput) żyje TU i jest
 * sterowana `KartotekaEngineConfig<TForm>`. Pilot (commercial/internal) staje
 * się jedną instancją configu odtwarzającą dziś-stan bajt-w-bajt — patrz
 * `documentEngineConfig.tsx` + wrapper `useDocumentRenderField.tsx`.
 *
 * Trzy różnice względem przed-silnikowego haka kartotek systemowych (usuniętego
 * przy migracji 4 kartotek na silnik), przeniesione 1:1 z pilota:
 *  1. Indeks pól po slugu ORAZ po id — layout seedowy adresuje slugiem
 *     snake_case, layout po „Wyślij" adresuje tenantowym id (UUID i snake_case
 *     nie kolidują).
 *  2. Pola RDZENIOWE (isCore) NIE zwracają null — routowane przez `config.fields`
 *     (custom renderer albo delegacja do ExtFieldValueInput z wartością z form).
 *  3. Fallback statyczny na BŁĄD zapytania `.../fields` (403 dla ról bez
 *     `crm_core.system_modules.view`) — pola core renderują się ZAWSZE z
 *     `config.fields`, ext zostaje niedostępne (parytet z przed-silnikowym
 *     zachowaniem, ADR-0071).
 */

import { type ReactNode, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';

import { Field } from '@/components/kartoteki/CoreFieldLayout';
import { ExtFieldValueInput } from '@/components/system-modules/ExtFieldValueInput';
import { api } from '@/lib/api';

import type {
  KartotekaEngineConfig,
  KartotekaRenderResult,
  KartotekaRenderRuntime,
} from './engine-config';
import type { EngineFieldsResponse, EngineFormField } from './engine-types';

export function useKartotekaRenderField<TForm, TExtra = undefined>(
  config: KartotekaEngineConfig<TForm, TExtra>,
  runtime: KartotekaRenderRuntime<TForm, TExtra>,
): KartotekaRenderResult {
  const { t } = useTranslation();

  const { moduleSlug, testIdPrefix, fields: fieldConfigs, labelSource } = config;
  const coerce = config.coerceEmptyToNull ?? false;
  const {
    form,
    onChange,
    extValues,
    onExtChange,
    editable,
    isCreate,
    extra,
    companyId,
    hasError,
    getError,
    clearError,
  } = runtime;
  const isFieldExtraRequired = runtime.isFieldExtraRequired ?? (() => false);

  // Ten sam queryKey co inni konsumenci katalogu pól modułu (`AddSystemFieldModal`,
  // `useDocumentItemExtFields`, …) — współdzielony cache metadanych pól per moduł.
  const fieldsQ = useQuery({
    queryKey: ['system-module-fields', moduleSlug],
    queryFn: async () => {
      const res = await api.get<EngineFieldsResponse>(`/system-modules/${moduleSlug}/fields`);
      return res.data.fields;
    },
    staleTime: 60_000,
  });

  // Fallback statyczny na błąd zapytania (403 — luka platformowa, nie do naprawy
  // tutaj). Pola core budowane z `config.fields` (id = slug w fallbacku), żeby
  // renderowały się ZAWSZE; ext (spoza config.fields) zostaje niedostępne
  // (parytet z przed-silnikowym zachowaniem, ADR-0071). Warunek na `isError`
  // (NIE `isLoading`) — podczas ładowania `fields` ma być puste jak dotychczas.
  const fallbackFields = useMemo<EngineFormField[]>(() => {
    if (!fieldsQ.isError) return [];
    return Object.entries(fieldConfigs).map(([slug, cfg]) => ({
      id: slug,
      slug,
      // `name` nie jest czytany dla pól core przy labelSource i18n (etykieta z
      // klucza t(...)), a dla labelSource 'definition' odwzorowuje brak metadanych.
      name: slug,
      fieldType: cfg.fieldType,
      description: null,
      isCore: true,
      isRequired: false,
      options: null,
      gcrecord: null,
    }));
  }, [fieldsQ.isError, fieldConfigs]);

  const effectiveFields = useMemo<EngineFormField[]>(() => {
    const base = fieldsQ.isError ? fallbackFields : (fieldsQ.data ?? []);
    const virtual = config.virtualFields ?? [];
    if (virtual.length === 0) return base;
    // Pola wirtualne (wyliczane, readonly) dokładane ZAWSZE — nie ma ich w katalogu
    // `/fields`, więc bez tego `renderField(slug)` zwracałby null (pole nie wyrenderowałoby
    // się po przeciągnięciu na layout). Dedup po slug/id, gdyby katalog jednak zawierał slug.
    const present = new Set(base.flatMap((f) => [f.slug, f.id]));
    const toAppend = virtual.filter((f) => !present.has(f.slug) && !present.has(f.id));
    return toAppend.length > 0 ? [...base, ...toAppend] : base;
  }, [fieldsQ.isError, fallbackFields, fieldsQ.data, config.virtualFields]);

  // Indeks po slugu ORAZ po id (UUID i snake_case nie kolidują). W fallbacku
  // slug i id są tożsame — nieszkodliwe, mapa i tak indeksuje po obu kluczach.
  const byKey = useMemo(() => {
    const map = new Map<string, EngineFormField>();
    for (const f of effectiveFields) {
      map.set(f.slug, f);
      map.set(f.id, f);
    }
    return map;
  }, [effectiveFields]);

  /** Odwzorowanie semantyki `v || null` pilota — TYLKO gdy config.coerceEmptyToNull. */
  function coerceValue(v: unknown): unknown {
    if (!coerce) return v;
    if (v === null || v === undefined || v === '') return null;
    return typeof v === 'string' ? v : String(v);
  }

  /** Atomowy zapis N pól nagłówka (klucz z unii wymaga asercji — nie literał). */
  function setField(patch: Partial<TForm>): void {
    onChange({ ...form, ...patch } as TForm);
  }

  function renderField(fieldId: string, settings?: Record<string, unknown>): ReactNode {
    const field = byKey.get(fieldId);
    // Pole nieznane (np. usunięte z modułu) lub miękko skasowane → nic nie renderuj.
    if (!field || field.gcrecord !== null) return null;

    // Pole read-only na WARTOŚCI (katalog `read_only`) → zablokowane niezależnie od trybu
    // strony. Wymuszamy centralnie w silniku, żeby honorowały je WSZYSTKIE gałęzie
    // (ext, custom renderer, natywna) — dzięki temu żadna kartoteka nie musi tego pilnować.
    const fieldEditable = editable && !field.readOnly;

    // Pole DODANE (ext) — delegacja do wspólnego edytora (jak precedens kartotek).
    // Nadpisania z buildera (settings.label / settings.required) mają pierwszeństwo
    // nad metadanymi pola — parytet z przed-silnikowym zachowaniem (hak usunięty przy migracji).
    if (!field.isCore) {
      const labelOverride = typeof settings?.label === 'string' ? settings.label.trim() : '';
      const label = labelOverride || field.name;
      const required =
        settings?.required != null
          ? Boolean(settings.required)
          : field.isRequired || isFieldExtraRequired(field);
      return (
        <Field
          label={label}
          required={required}
          colSpan={field.fieldType === 'text' ? 2 : undefined}
          description={field.description ?? undefined}
          error={getError?.(field.slug as keyof TForm)}
        >
          <ExtFieldValueInput
            field={field}
            value={extValues[field.slug]}
            onChange={(v) => onExtChange(field.slug, v)}
            disabled={!fieldEditable}
            data-testid={`${testIdPrefix}-${field.slug}`}
            recordValues={extValues}
          />
        </Field>
      );
    }

    // Pole RDZENIOWE — routing przez config kartoteki.
    const cfg = fieldConfigs[field.slug];
    if (!cfg) {
      // Core-slug spoza configu — amber warning-box, te same klasy co default
      // branch ExtFieldValueInput (jeden wspólny kontrakt silnika).
      return (
        <div
          className="rounded border border-amber-300 bg-amber-50 p-2 text-xs text-amber-800 dark:border-amber-700 dark:bg-amber-900/20 dark:text-amber-300"
          data-testid={`${testIdPrefix}-${field.slug}`}
        >
          {t('common.extField.unsupportedType', { type: field.slug })}
        </div>
      );
    }

    // Strukturalne (np. documentTypeId) — badge renderuje nagłówek strony, poza drzewem.
    if (cfg.structuralNull) return null;

    // Nadpisanie (custom widget) — status enum, DictionarySelect, async NIP itd.
    if (cfg.renderer) {
      return cfg.renderer({
        field,
        value: cfg.stateKey ? form[cfg.stateKey] : undefined,
        form,
        setField,
        editable: fieldEditable,
        isCreate,
        testIdPrefix,
        companyId,
        hasError,
        getError,
        clearError,
        isFieldExtraRequired,
        extra,
        t,
      });
    }

    // Natywne pole core — wspólny edytor per fieldType (delegacja do ExtFieldValueInput).
    // Bez stateKey nie ma czego wiązać (defensywnie — nie powinno wystąpić dla natywnego).
    if (!cfg.stateKey) return null;
    const stateKey = cfg.stateKey;
    // Nazwa pola systemowego NIE jest zmieniana z buildera (chcesz inną nazwę → pole dodatkowe).
    const label = typeof labelSource === 'function' ? labelSource({ field, cfg, t }) : field.name;
    return (
      <Field
        label={label}
        colSpan={cfg.colSpan}
        required={isFieldExtraRequired(field)}
        error={getError?.(stateKey)}
      >
        <ExtFieldValueInput
          field={field}
          value={form[stateKey]}
          onChange={(v) => setField({ [stateKey]: coerceValue(v) } as Partial<TForm>)}
          disabled={!fieldEditable}
          maxLength={cfg.maxLength}
          rows={cfg.rows}
          invalid={hasError?.(stateKey) ?? false}
          data-testid={`${testIdPrefix}-${String(stateKey)}`}
        />
      </Field>
    );
  }

  return { renderField, fields: effectiveFields, isLoading: fieldsQ.isLoading };
}
